import { useEffect, useMemo, useState } from 'react';
import { FiEdit3, FiSave, FiTrash2, FiX } from 'react-icons/fi';
import { TableHeader, TablePerPage } from '../../../../../_CORE/APP/Templates/_UI/Table/_index';
import { FooterPagination } from '../../../../../_CORE/APP/Templates/Layouts/Section/Table/_Plugins/FooterPagination';
import { EmailSectionLayout } from '../_UI/EmailSectionLayout';
import { EmailFieldHint, EmailFieldLabel } from '../_UI/EmailFieldGuide';
import { useEmailAdmin } from '../_Hooks/useEmailAdmin';
import { useEmailEditorNavigation } from '../_Hooks/useEmailEditorNavigation';
import { EMAIL_ADMIN_CONSTANTS as C } from '../_MODULES/_API';
import { confirm } from '../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost';

const EMPTY = { first_name: '', last_name: '', email: '', company: '', role: '', tags: '', source: 'manual', active: true };
const PER_PAGE = 12;

const norm = (value = '') => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const tokens = (value) => Array.isArray(value) ? value : String(value || '').split(/[,;|]+/).map((x) => x.trim()).filter(Boolean);
const dateKey = (value) => String(value || '').slice(0, 10);

const optionsFrom = (items, getter) => [...new Set(items.flatMap((item) => tokens(getter(item))).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
    .map((value) => ({ value, label: value }));

export const EmailContactsView = () => {
    const api = useEmailAdmin();
    const { editorRef, goToEditor } = useEmailEditorNavigation();
    const [items, setItems] = useState([]);
    const [message, setMessage] = useState('');
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({ active: null, company: [], tags: [], lists: [], date: null, range: [null, null] });
    const [order, setOrder] = useState('updated-desc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(PER_PAGE);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [values, setValues] = useState(EMPTY);

    const load = async () => {
        try { setItems(await api.get(C.CONTACTS) || []); setMessage(''); }
        catch (err) { setMessage(err.message); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const dynamic = useMemo(() => ({
        companies: optionsFrom(items, (item) => item.company),
        tags: optionsFrom(items, (item) => item.tags),
        lists: optionsFrom(items, (item) => item.list_names),
    }), [items]);

    const headerFilters = useMemo(() => [
        { key: 'active', label: 'Estado', type: 'select', value: filters.active, onChange: (v) => setFilters((p) => ({ ...p, active: v })), options: [{ value: '1', label: 'Activo' }, { value: '0', label: 'Inactivo' }], placeholder: 'Todos' },
        { key: 'company', label: 'Empresa', type: 'multi', value: filters.company, onChange: (v) => setFilters((p) => ({ ...p, company: v || [] })), options: dynamic.companies, placeholder: 'Seleccionar empresas…' },
        { key: 'tags', label: 'Tags', type: 'multi', value: filters.tags, onChange: (v) => setFilters((p) => ({ ...p, tags: v || [] })), options: dynamic.tags, placeholder: 'Seleccionar tags…' },
        { key: 'lists', label: 'Listas', type: 'multi', value: filters.lists, onChange: (v) => setFilters((p) => ({ ...p, lists: v || [] })), options: dynamic.lists, placeholder: 'Seleccionar listas…' },
        { key: 'date', label: 'Fecha exacta', type: 'date', value: filters.date, onChange: (v) => setFilters((p) => ({ ...p, date: v })), placeholder: 'Alta del contacto' },
        { key: 'range', label: 'Rango de alta', type: 'dateRange', col: 2, value: filters.range, onChange: (v) => setFilters((p) => ({ ...p, range: v || [null, null] })), placeholder: 'Desde — hasta' },
    ], [filters, dynamic]);

    const orderOptions = [
        { value: 'updated-desc', label: 'Actualizados recientes' },
        { value: 'created-desc', label: 'Altas recientes' },
        { value: 'email-asc', label: 'Email A → Z' },
        { value: 'name-asc', label: 'Nombre A → Z' },
    ];

    const filtered = useMemo(() => {
        const term = norm(search.trim());
        const companyValues = (filters.company || []).map((v) => norm(v?.value ?? v));
        const tagValues = (filters.tags || []).map((v) => norm(v?.value ?? v));
        const listValues = (filters.lists || []).map((v) => norm(v?.value ?? v));
        const activeValue = filters.active?.value ?? filters.active;
        const exact = filters.date ? dateKey(filters.date instanceof Date ? `${filters.date.getFullYear()}-${String(filters.date.getMonth()+1).padStart(2,'0')}-${String(filters.date.getDate()).padStart(2,'0')}` : filters.date) : '';
        const [from, to] = filters.range || [null, null];
        const fromTime = from instanceof Date ? new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime() : null;
        const toTime = to instanceof Date ? new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999).getTime() : null;

        const rows = items.filter((item) => {
            if (term && !norm([item.first_name, item.last_name, item.email, item.company, item.role, ...(item.tags || []), ...(item.list_names || [])].join(' ')).includes(term)) return false;
            if (activeValue !== null && activeValue !== undefined && activeValue !== '' && String(item.active ? 1 : 0) !== String(activeValue)) return false;
            if (companyValues.length && !companyValues.includes(norm(item.company))) return false;
            if (tagValues.length) {
                const current = (item.tags || []).map(norm);
                if (!tagValues.some((tag) => current.includes(tag))) return false;
            }
            if (listValues.length) {
                const currentLists = (item.list_names || []).map(norm);
                if (!listValues.some((name) => currentLists.includes(name))) return false;
            }
            if (exact && dateKey(item.created_at) !== exact) return false;
            if (fromTime || toTime) {
                const current = new Date(item.created_at).getTime();
                if (Number.isNaN(current)) return false;
                if (fromTime && current < fromTime) return false;
                if (toTime && current > toTime) return false;
            }
            return true;
        });

        return [...rows].sort((a, b) => {
            if (order === 'email-asc') return String(a.email).localeCompare(String(b.email));
            if (order === 'name-asc') return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
            if (order === 'created-desc') return new Date(b.created_at) - new Date(a.created_at);
            return new Date(b.updated_at) - new Date(a.updated_at);
        });
    }, [items, search, filters, order]);

    useEffect(() => { setPage(1); }, [search, filters, order, perPage]);
    const totalPages = perPage === -1 ? 1 : Math.max(1, Math.ceil(filtered.length / perPage));
    const safePage = Math.min(page, totalPages);
    const visible = perPage === -1 ? filtered : filtered.slice((safePage - 1) * perPage, safePage * perPage);

    const startCreate = () => { setEditing(null); setValues(EMPTY); setFormOpen(true); setMessage(''); goToEditor(); };
    const startEdit = (item) => { setEditing(item); setValues({ ...item, tags: (item.tags || []).join(', ') }); setFormOpen(true); setMessage(''); goToEditor(); };
    const submit = async (event) => {
        event.preventDefault();
        try {
            await api.securePost(C.CONTACT_SAVE, { ...values, id: editing?.id || undefined, active: values.active ? 1 : 0 });
            setFormOpen(false); setEditing(null); await load();
        } catch (err) { setMessage(err.message); }
    };
    const remove = async (item) => {
        if (!(await confirm.danger(`Eliminar ${item.email}?`, 'El contacto sera eliminado del administrador. Esta accion no se puede deshacer.'))) return;
        try { await api.securePost(C.CONTACT_REMOVE, { id: item.id }); await load(); }
        catch (err) { setMessage(err.message); }
    };

    const from = filtered.length ? (perPage === -1 ? 1 : ((safePage - 1) * perPage) + 1) : 0;
    const to = filtered.length ? (perPage === -1 ? filtered.length : Math.min(safePage * perPage, filtered.length)) : 0;

    return (
        <EmailSectionLayout title="Contactos" description="Base de destinatarios deduplicada, editable y filtrable para tus campañas.">
            <section className="PBADMIN__panel PBMAIL__panel">
                <div className="PBADMIN__collection_tools PBMAIL__collection_tools">
                    <TableHeader
                        pill="CONTACTOS"
                        title="Administrar"
                        titleAccent="destinatarios"
                        newLabel="Nuevo"
                        onNew={startCreate}
                        search={search}
                        onSearch={setSearch}
                        searchPlaceholder="Buscar nombre, email, empresa, puesto o tag…"
                        orderOptions={orderOptions}
                        order={order}
                        onOrder={setOrder}
                        filters={headerFilters}
                        onClearFilters={() => { setSearch(''); setFilters({ active: null, company: [], tags: [], lists: [], date: null, range: [null, null] }); }}
                        stats={<div className="PBADMIN__filter_summary"><span><strong>{filtered.length}</strong> resultado{filtered.length === 1 ? '' : 's'}</span><span>{items.length} total</span></div>}
                    />
                </div>

                {message && <div className="PBADMIN__alert">{message}</div>}

                {formOpen && (
                    <form ref={editorRef} className="PBADMIN__editor PBADMIN__form_grid PBMAIL__form PBMAIL__editor" onSubmit={submit}>
                        <div className="PBADMIN__editor_head"><strong>{editing ? 'Editar contacto' : 'Nuevo contacto'}</strong><button type="button" onClick={() => setFormOpen(false)}><FiX /></button></div>
                        <label className="PBADMIN__field">
                            <EmailFieldLabel status="optional">Nombre</EmailFieldLabel>
                            <input placeholder="Ej: María" value={values.first_name || ''} onChange={(e) => setValues((p) => ({ ...p, first_name: e.target.value }))} />
                            <EmailFieldHint>Sirve para personalizar mensajes con {'{{nombre}}'}.</EmailFieldHint>
                        </label>
                        <label className="PBADMIN__field">
                            <EmailFieldLabel status="optional">Apellido</EmailFieldLabel>
                            <input placeholder="Ej: González" value={values.last_name || ''} onChange={(e) => setValues((p) => ({ ...p, last_name: e.target.value }))} />
                            <EmailFieldHint>Complementa el nombre completo del destinatario.</EmailFieldHint>
                        </label>
                        <label className="PBADMIN__field PBADMIN__field--2">
                            <EmailFieldLabel status="required">Email</EmailFieldLabel>
                            <input type="email" required placeholder="Ej: maria.gonzalez@empresa.com" value={values.email || ''} onChange={(e) => setValues((p) => ({ ...p, email: e.target.value }))} />
                            <EmailFieldHint>Es el unico dato imprescindible para guardar y enviar.</EmailFieldHint>
                        </label>
                        <label className="PBADMIN__field">
                            <EmailFieldLabel status="recommended">Empresa</EmailFieldLabel>
                            <input placeholder="Ej: Globant" value={values.company || ''} onChange={(e) => setValues((p) => ({ ...p, company: e.target.value }))} />
                            <EmailFieldHint>Te permite filtrar y personalizar por empresa.</EmailFieldHint>
                        </label>
                        <label className="PBADMIN__field">
                            <EmailFieldLabel status="recommended">Puesto / rol</EmailFieldLabel>
                            <input placeholder="Ej: Talent Acquisition / RRHH" value={values.role || ''} onChange={(e) => setValues((p) => ({ ...p, role: e.target.value }))} />
                            <EmailFieldHint>Ejemplo: Recruiter, RRHH, CTO, Hiring Manager.</EmailFieldHint>
                        </label>
                        <label className="PBADMIN__field">
                            <EmailFieldLabel status="recommended">Tags</EmailFieldLabel>
                            <input placeholder="Ej: rrhh, software, argentina" value={values.tags || ''} onChange={(e) => setValues((p) => ({ ...p, tags: e.target.value }))} />
                            <EmailFieldHint>Separalos por coma. Sirven para segmentar y buscar.</EmailFieldHint>
                        </label>
                        <label className="PBADMIN__field">
                            <EmailFieldLabel status="optional">Fuente</EmailFieldLabel>
                            <input placeholder="Ej: manual, LinkedIn, web empresa" value={values.source || ''} onChange={(e) => setValues((p) => ({ ...p, source: e.target.value }))} />
                            <EmailFieldHint>Solo indica de donde obtuviste el contacto.</EmailFieldHint>
                        </label>
                        <div className="PBMAIL__check_group PBADMIN__field--2">
                            <label className="PBADMIN__check"><input type="checkbox" checked={!!values.active} onChange={(e) => setValues((p) => ({ ...p, active: e.target.checked }))} /><span>Contacto activo</span></label>
                            <EmailFieldHint>Dejalo activo si queres que pueda entrar en futuras campanas.</EmailFieldHint>
                        </div>
                        <div className="PBADMIN__form_footer"><button className="PBADMIN__primary" type="submit" disabled={api.isLoading}><FiSave /> Guardar</button><button type="button" className="PBADMIN__secondary" onClick={() => setFormOpen(false)}>Cancelar</button></div>
                    </form>
                )}

                <div className="PBMAIL__table">
                    {visible.length === 0 && !api.isLoading && <div className="PBADMIN__empty">No hay contactos para mostrar.</div>}
                    {visible.map((item) => (
                        <article className="PBMAIL__contact_row" key={item.id}>
                            <div className="PBMAIL__contact_main"><strong>{[item.first_name, item.last_name].filter(Boolean).join(' ') || item.email}</strong><a href={`mailto:${item.email}`}>{item.email}</a><span>{[item.role, item.company].filter(Boolean).join(' · ') || 'Sin empresa / puesto'}</span></div>
                            <div className="PBMAIL__chips">{(item.tags || []).slice(0, 4).map((tag) => <span key={`tag-${tag}`}>{tag}</span>)}{(item.list_names || []).slice(0, 3).map((name) => <span className="is-list" key={`list-${name}`}>{name}</span>)}</div>
                            <div className="PBMAIL__row_meta"><span className={`PBMAIL__status ${item.active ? 'is-active' : 'is-inactive'}`}>{item.active ? 'Activo' : 'Inactivo'}</span><small>{item.source || 'manual'}</small></div>
                            <div className="PBADMIN__row_actions"><button type="button" onClick={() => startEdit(item)} title="Editar"><FiEdit3 /></button><button type="button" onClick={() => remove(item)} className="is-danger" title="Eliminar"><FiTrash2 /></button></div>
                        </article>
                    ))}
                </div>

                {filtered.length > 0 && <div className="PBADMIN__collection_footer"><div className="PBADMIN__collection_count">Mostrando <strong>{from}–{to}</strong> de <strong>{filtered.length}</strong></div><TablePerPage value={perPage} onChange={setPerPage} totalItems={filtered.length} /><FooterPagination pagina={safePage} totalPaginas={totalPages} onIr={setPage} /></div>}
            </section>
        </EmailSectionLayout>
    );
};
